import { usePublishEvent } from '@/analytics/usePublishEvent';
import { notifyError } from '@/common/utils/notifyError';
import { useSessionConfig } from '@/store/sessionConfigStore';
import { useSignInStore } from '@/store/signInStore';
import { v4 as uuidv4 } from 'uuid';

const tmxVersion = 3;
const tmxSelector = 'threatmetrix-tags-script';
const tmxOrgId = process.env.THREATMETRIX_ORG_ID;

/* istanbul ignore next */
const createBeacon = async (sessionId: string, onError: () => void) => {
  const connectionStr = 'turn:aa.online-metrix.net?transport=';
  const userName = `1:${tmxOrgId}:${sessionId}`;
  const serverConfig = {
    iceServers: [
      {
        urls: `${connectionStr}tcp`,
        username: userName,
        credential: sessionId,
      },
      {
        urls: `${connectionStr}udp`,
        username: userName,
        credential: sessionId,
      },
    ],
  };

  try {
    const peerConnection = new RTCPeerConnection(serverConfig);
    peerConnection.createDataChannel(Math.random().toString());

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);
  } catch (error) {
    notifyError(error);
    onError();
  }
};

const buildUrl = (fileExt: string, sessionId: string) => {
  const randomConst = 2742745743359;
  function random() {
    return Math.floor(randomConst * Math.random());
  }

  function offset(offset: number) {
    return (offset + 78364164096).toString(36);
  }

  function final() {
    return offset(random());
  }

  const m = random(),
    l = 885187064159;
  let u = random();

  //Random offsets for org id param key metadata tracking
  const randOffset1 = ((u = u - (u % 256) + tmxVersion) + m) % randomConst;
  const randOffset2 = (l + m) % randomConst;

  const url = new URL(
    `${final() + offset(m)}.${fileExt}`,
    'https://content.asurion53.com',
  );
  url.searchParams.set(offset(randOffset2) + offset(randOffset1), tmxOrgId);
  url.searchParams.set(final() + final(), sessionId);
  return url.toString();
};

const loadScript = (sessionId: string) => {
  const nonce = uuidv4();

  const headElement = document.getElementsByTagName('head').item(0);
  const script = document.createElement('script');

  script.id = tmxSelector;
  script.type = 'text/javascript';
  script.src = buildUrl('js', sessionId);
  script.setAttribute('nonce', nonce);

  headElement?.appendChild(script);

  return script;
};

const loadIframe = (sessionId: string) => {
  const nonce = uuidv4();

  const iframe = document.createElement('iframe');

  iframe.title = 'tmx iframe';
  iframe.width = '0';
  iframe.height = '0';
  iframe.setAttribute('nonce', nonce);
  iframe.setAttribute('aria-disabled', 'true');
  iframe.setAttribute(
    'style',
    'color:rgba(0,0,0,0); float:left; position:absolute; top:-200; left:-200; border:0px',
  );
  iframe.src = buildUrl('htm', sessionId);

  document.body.appendChild(iframe);

  return iframe;
};

/* istanbul ignore next */
const removeElements = () => {
  const tmxScript = document.getElementById(tmxSelector);
  tmxScript?.parentElement?.removeChild(tmxScript);

  const hp_frame = document.getElementById('tdz_ifrm');
  if (hp_frame) {
    document.body.removeChild(hp_frame);
  }

  const tmx_frame = document.getElementById('tmx_tags_iframe');
  if (tmx_frame) {
    document.body.removeChild(tmx_frame);
  }
};
/* istanbul ignore next */
const loadThreatMetrix = (
  sessionId: string,
  onSuccess: () => void,
  onError: () => void,
) => {
  removeElements();

  const scriptElement = loadScript(sessionId);
  loadIframe(sessionId);

  scriptElement.onerror = () => {
    removeElements();
    onError();
  };

  scriptElement.onload = () => {
    onSuccess();
  };
};

export const useThreatMetrix = () => {
  const updateSession = useSignInStore((s) => s.updateSession);
  const sessionId = useSignInStore((s) => s.session.lexisNexisSessionId);
  const publishEvent = usePublishEvent();
  const tmxEnabled = useSessionConfig((s) => s.threatMetrixEnabled);

  const publish = (event: string) => publishEvent(event, tmxSelector);

  return async () => {
    if (!tmxEnabled) {
      return undefined;
    }

    let id;

    if (!sessionId) {
      id = uuidv4().replaceAll('-', '');
      updateSession({ lexisNexisSessionId: id });
    } else {
      id = sessionId;
    }

    createBeacon(id, () => publish('AsurionID_ThreatMetrixBeaconLoadFailed'));

    loadThreatMetrix(
      id,
      /* istanbul ignore next */
      () => publish('AsurionID_ThreatMetrixLoaded'),
      /* istanbul ignore next */
      () => publish('AsurionID_ThreatMetrixLoadFailed'),
    );

    removeElements();
    return id;
  };
};
